// #816 — prospective-client record upload, PUBLIC page (Cluster C, 2026-08-31).
//
// Route: /records/:publicId#t=<token>   (public — no Layout, no auth)
//
// Mirrors QuestionnaireFill.js, with the differences the panel insisted on:
//   • the token lives in the URL FRAGMENT.  Read on mount, persisted to
//     sessionStorage (tab-scoped) so a refresh survives, and
//     history.replaceState-scrubbed BEFORE the first API call so it never
//     reaches a Referer header, the address bar, or a server log.
//   • after POST /verify the HttpOnly path-scoped SESSION COOKIE is the
//     credential (panel D6(d)).  /consent and /upload send NO token — the
//     bearer is not retransmitted per file.
//   • the capability is WRITE-ONLY (D5).  There is no listing endpoint and we
//     never ask for one.  The "N sent" count on this page is CLIENT-HELD,
//     persisted to sessionStorage, exactly as §6/S3 resolved it — a forwarded
//     link must not become a read oracle on someone else's medical records.
//   • one XHR per file (D32) so file 47 of 60 can be retried without losing
//     the batch, with real upload progress (fetch cannot report it).
//
// The consent copy and the retention DATE are rendered verbatim from /verify
// (D25).  This page never composes its own version of the firm's promise.
//
// States: loading / missing_token / unavailable / email_code / consent /
//         upload / error   (+ a `locked` banner that never unmounts the page)
//
// Mobile-first because that is the actual device: a distressed parent
// photographing 80 pages of a CPS report on a phone, on LTE.  Single column,
// large tap targets, sticky action bar.  No window.confirm / prompt / alert.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

// Extensions we let through client-side.  The SERVER is authoritative — it
// sniffs magic bytes (D4) — so this is only a courtesy that saves someone a
// 25 MB upload of a .zip we will refuse anyway.  Deliberately generous: an
// iPhone often reports an empty MIME type for HEIC, so never gate on MIME.
const ACCEPT_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff', 'heic', 'heif']
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.gif,.tif,.tiff,.heic,.heif,application/pdf,image/*'
const FALLBACK_MAX_BYTES = 25 * 1024 * 1024

function apiBase () {
  const cfg = process.env.REACT_APP_API_URL || ''
  try { if (cfg && new URL(cfg).origin === window.location.origin) return '' } catch { /* fall through */ }
  return cfg
}

async function call (path, body) {
  const res = await fetch(`${apiBase()}/api/public/lead-records/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    credentials: 'include',            // the HttpOnly session cookie set by /verify
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  let data = null
  try { data = await res.json() } catch { data = null }
  return { ok: res.ok, status: res.status, data: data || {} }
}

// fetch() cannot report upload progress, and this page exists for people
// pushing 8 MB photos over LTE who need to see that something is happening.
function uploadOne ({ publicId, file, onProgress }) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    const fd = new FormData()
    fd.append('file', file, file.name)
    xhr.open('POST', `${apiBase()}/api/public/lead-records/${publicId}/upload`)
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)))
    }
    const finish = (status, text) => {
      let data = null
      try { data = JSON.parse(text) } catch { data = null }
      resolve({ ok: status >= 200 && status < 300, status, data: data || {} })
    }
    xhr.onload = () => finish(xhr.status, xhr.responseText)
    xhr.onerror = () => resolve({ ok: false, status: 0, data: {} })
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: {} })
    xhr.onabort = () => resolve({ ok: false, status: 0, data: {} })
    xhr.send(fd)
  })
}

function fmtBytes (n) {
  const b = Number(n) || 0
  if (b < 1024) return `${b} B`
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / 1048576).toFixed(1)} MB`
}
function extOf (name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ''))
  return m ? m[1].toLowerCase() : ''
}
function fmtTime (iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) } catch { return '' }
}
// "wait about 300 seconds" is the sort of sentence that makes a person give up
// and phone the office. The per-IP window alone is five minutes, so this path
// was already producing it before any of the new caps existed.
function waitPhrase (seconds) {
  const s = Number(seconds) || 60
  if (s < 120) return `${s} seconds`
  const m = Math.round(s / 60)
  if (m < 120) return `${m} minute${m === 1 ? '' : 's'}`
  const h = Math.round(m / 60)
  return `${h} hour${h === 1 ? '' : 's'}`
}

// Module-scope shells — NEVER define these inside the component: a new
// component type per render remounts the subtree and every keystroke would
// drop focus in the initials field.
function Shell ({ children }) {
  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {children}
      </div>
    </div>
  )
}
function Header ({ firmName, title, subtitle }) {
  return (
    <div className="border-b border-gray-200 px-5 py-4 bg-gray-50">
      {firmName && <div className="text-xs uppercase tracking-wide text-gray-500">{firmName}</div>}
      <h1 className="text-lg font-bold">{title}</h1>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  )
}
function Centered ({ icon, title, children }) {
  return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3" aria-hidden="true">{icon}</div>
        <h1 className="text-xl font-bold mb-2">{title}</h1>
        {children}
      </div>
    </Shell>
  )
}
function StatusPill ({ status }) {
  const map = {
    queued:    { label: 'Waiting',   cls: 'bg-gray-100 text-gray-600' },
    uploading: { label: 'Sending',   cls: 'bg-sky-100 text-sky-800' },
    done:      { label: 'Sent',      cls: 'bg-emerald-100 text-emerald-800' },
    failed:    { label: 'Not sent',  cls: 'bg-red-100 text-red-800' },
  }
  const s = map[status] || map.queued
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}>{s.label}</span>
}

export default function RecordUpload () {
  const { publicId } = useParams()
  const tokenKey = `records_token:${publicId}`
  const sentKey = `records_sent:${publicId}`

  // 1) fragment → sessionStorage → scrub (before any API call)
  const token = useMemo(() => {
    const raw = window.location.hash || ''
    const m = raw.match(/^#(?:t=)?([A-Za-z0-9_\-.=]+)$/)
    if (m) {
      try { sessionStorage.setItem(tokenKey, m[1]) } catch { /* private mode */ }
      return m[1]
    }
    try { return sessionStorage.getItem(tokenKey) || '' } catch { return '' }
  }, [tokenKey])
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
  }, [])

  const [stage, setStage] = useState('loading')
  const [error, setError] = useState('')
  const [firmName, setFirmName] = useState('')
  const [info, setInfo] = useState(null)         // the /verify payload
  // Panel D5 / §6-S3: this list is CLIENT-HELD.  We never ask the server what
  // it already has, because a forwarded link must not read back someone else's
  // documents.  It survives a reload so a dropped connection does not make the
  // uploader re-send everything (D32).
  const [sent, setSent] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(sentKey) || '[]') } catch { return [] }
  })
  // Mid-session death (revoked / expired).  Mirrors the questionnaire's
  // verifier B-4 fix: NEVER unmount — the receipts below are the uploader's
  // only proof of what they already sent.
  const [locked, setLocked] = useState('')
  const [busy, setBusy] = useState(false)

  // consent
  const [consentChecked, setConsentChecked] = useState(false)
  const [initials, setInitials] = useState('')

  // email code
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [cooldownEnds, setCooldownEnds] = useState(0)
  const [now, setNow] = useState(Date.now())

  // upload queue — held in a ref so the sequential pump never reads a stale
  // closure, with a counter to force renders.
  const [unsaved, setUnsaved] = useState(false)
  const queueRef = useRef([])
  const [, setTick] = useState(0)
  const bump = useCallback(() => {
    setTick(t => t + 1)
    setUnsaved(queueRef.current.some(i => i.status === 'uploading' || i.status === 'queued'))
  }, [])
  const pumpingRef = useRef(false)
  const fileInputRef = useRef(null)

  useEffect(() => { document.title = firmName ? `${firmName} — Send your records` : 'Send your records' }, [firmName])
  useEffect(() => {
    try { sessionStorage.setItem(sentKey, JSON.stringify(sent.slice(-200))) } catch { /* private mode */ }
  }, [sent, sentKey])

  // A reload mid-batch loses the files still waiting (the browser will not
  // hand back a File handle across a navigation).  The receipts below survive;
  // the queue does not — so say so before it happens.
  useEffect(() => {
    if (!unsaved) return undefined
    const h = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [unsaved])

  useEffect(() => {
    if (cooldownEnds <= now) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [cooldownEnds, now])
  const cooldownLeft = Math.max(0, Math.ceil((cooldownEnds - now) / 1000))

  const maxBytes = info?.max_file_bytes || FALLBACK_MAX_BYTES
  const uploadsOpen = info ? info.uploads_open !== false : true

  const applyVerified = useCallback((d) => {
    setInfo(d)
    setFirmName(d.firm_name || '')
    setStage(d.consent_required ? 'consent' : 'upload')
  }, [])

  // Every unknown / expired / revoked link answers with the SAME 404 as a
  // stranger — there is no oracle to read here, so the page shows one message.
  const routeFailure = useCallback((r, { fromWrite = false } = {}) => {
    if (r.status === 404) {
      if (fromWrite && info) {
        setLocked('This link is no longer active — the firm may have replaced or cancelled it. Anything listed below as “Sent” did reach the firm. Please call the office for a new link.')
        return true
      }
      setStage('unavailable')
      return true
    }
    return false
  }, [info])

  const verify = useCallback(async (extra = {}) => {
    const r = await call(`${publicId}/verify`, { token, ...extra })
    if (r.ok) { applyVerified(r.data); return { ok: true } }
    if (r.status === 401) return { ok: false, codeStage: true, r }
    if (routeFailure(r)) return { ok: false, routed: true }
    return { ok: false, r }
  }, [publicId, token, applyVerified, routeFailure])

  useEffect(() => {
    let cancelled = false
    async function boot () {
      try {
        const shell = await call(publicId)
        if (cancelled) return
        if (shell.ok) setFirmName(shell.data.firm_name || '')
        if (!token) { setStage('missing_token'); return }
        const v = await verify()
        if (cancelled) return
        if (v.ok || v.routed) return
        if (v.codeStage) {
          // require_email_code is on: the server will not open the link until
          // the 6-digit code is right.
          setStage('email_code')
          if (v.r?.data?.error === 'code_expired') setError('That code has expired. Send a new one.')
          return
        }
        setStage('error')
        setError('We could not open this page. Please use the exact link from the email.')
      } catch {
        if (cancelled) return
        setStage('error')
        setError('We could not reach the firm’s server. Please check your connection and try again.')
      }
    }
    boot()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, token])

  // ── email code ──
  const requestCode = async () => {
    setError(''); setBusy(true)
    try {
      const r = await call(`${publicId}/request-code`, { token })
      if (r.ok) { setCodeSent(true); setCode(''); setCooldownEnds(Date.now() + 30000) }
      else if (r.status === 429) setError('Too many code requests. Please call the office.')
      else if (!routeFailure(r)) setError('Could not send the code. Please try again.')
    } catch {
      setError('Could not reach the server. Please try again.')
    } finally { setBusy(false) }
  }
  const submitCode = async (e) => {
    e.preventDefault()
    setError(''); setBusy(true)
    try {
      const v = await verify({ code: code.trim() })
      if (v.ok || v.routed) return
      if (v.codeStage) setError(v.r?.data?.message || 'That code is not right.')
      else setError('That code did not work.')
    } catch {
      setError('Could not reach the server. Please try again.')
    } finally { setBusy(false) }
  }

  // ── consent ──
  const acceptConsent = async (e) => {
    e.preventDefault()
    setError(''); setBusy(true)
    try {
      const r = await call(`${publicId}/consent`, { initials: initials.trim() })
      if (r.ok) { setInfo(i => ({ ...i, consent_required: false })); setStage('upload'); return }
      if (routeFailure(r, { fromWrite: true })) return
      setError(r.data?.message || 'Please type your initials to continue.')
    } catch {
      setError('Could not reach the server. Please try again.')
    } finally { setBusy(false) }
  }

  // ── the queue ──
  const pump = useCallback(async () => {
    if (pumpingRef.current) return
    pumpingRef.current = true
    try {
      for (;;) {
        const item = queueRef.current.find(i => i.status === 'queued')
        if (!item) break
        item.status = 'uploading'; item.pct = 0; item.error = ''
        bump()

        let r = await uploadOne({
          publicId, file: item.file,
          onProgress: (p) => { item.pct = p; bump() },
        })

        // The upload route answers 404 for BOTH a dead link and an expired
        // session (60 min), deliberately — it must not distinguish them for a
        // stranger.  So try exactly one silent re-verify with the stored token:
        // if the link is alive this re-mints the cookie and the file goes; if
        // it is genuinely dead we fall through to the locked banner.
        if (r.status === 404 && token) {
          const again = await call(`${publicId}/verify`, { token })
          if (again.ok) {
            setInfo(again.data)
            if (again.data.consent_required) {
              item.status = 'queued'; bump()
              setStage('consent')
              break
            }
            r = await uploadOne({
              publicId, file: item.file,
              onProgress: (p) => { item.pct = p; bump() },
            })
          }
        }

        if (r.ok) {
          item.status = 'done'; item.pct = 100
          const receipt = r.data.receipt || {}
          setSent(s => [...s, {
            name: item.name, size: Number(receipt.size_bytes ?? item.size),
            sha256: receipt.sha256 || '', at: receipt.received_at || new Date().toISOString(),
          }])
          bump()
          continue
        }

        item.status = 'failed'
        if (r.status === 404) {
          item.error = 'The link closed before this file finished.'
          setLocked('This link is no longer active — the firm may have replaced or cancelled it. Anything listed below as “Sent” did reach the firm. Please call the office for a new link.')
          bump(); break
        }
        if (r.status === 409) {
          item.status = 'queued'; item.error = ''
          bump(); setStage('consent'); break
        }
        if (r.status === 503) {
          item.status = 'queued'
          setInfo(i => (i ? { ...i, uploads_open: false, uploads_closed_message: r.data?.message || null } : i))
          bump(); break
        }
        if (r.status === 429) {
          item.status = 'queued'
          item.error = ''
          if (r.data?.error === 'rejected_cap') {
            // Not a burst — a run of files the scanner refused, so sending is
            // paused for long enough that "press Send waiting files" is the
            // wrong instruction. Close the picker the way an unavailable
            // scanner does, and say why.
            setInfo(i => (i ? { ...i, uploads_open: false, uploads_closed_message: r.data?.message || null } : i))
          } else {
            setError(`Too many uploads at once. Please wait about ${waitPhrase(r.data?.retry_after_seconds)}, then press “Send waiting files”.`)
          }
          bump(); break
        }
        // A refused file that also closed the door — the server says so on the
        // same response rather than making the page ask again.
        if (r.data && r.data.uploads_open === false) {
          setInfo(i => (i ? { ...i, uploads_open: false, uploads_closed_message: r.data.uploads_closed_message || null } : i))
        }
        item.error = r.status === 0
          ? 'The connection dropped before this file finished. Press “Retry”.'
          : (r.data?.message || 'That file was not accepted. Press “Retry”, or call the office.')
        bump()
      }
    } finally {
      pumpingRef.current = false
      bump()
    }
  }, [publicId, token, bump])

  const addFiles = (fileList) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    for (const f of files) {
      const bad = f.size > maxBytes
        ? `This file is ${fmtBytes(f.size)}. The limit is ${fmtBytes(maxBytes)} per file — please send it in parts, or call the office.`
        : (!ACCEPT_EXT.includes(extOf(f.name)) && !/^(image\/|application\/pdf)/.test(f.type || ''))
            ? 'We can accept PDFs and photos (JPEG, PNG, GIF, TIFF, HEIC). Zip files and documents are not accepted — please send the pages themselves.'
            : ''
      queueRef.current.push({
        key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: bad ? null : f,           // drop the handle when we already know it fails
        name: f.name, size: f.size,
        status: bad ? 'failed' : 'queued', pct: 0, error: bad,
      })
    }
    bump()
    if (fileInputRef.current) fileInputRef.current.value = ''   // allow re-picking the same file
    pump()
  }

  const retry = (key) => {
    const item = queueRef.current.find(i => i.key === key)
    if (!item || !item.file) return
    item.status = 'queued'; item.error = ''; item.pct = 0
    setError('')
    bump(); pump()
  }
  const removeFromQueue = (key) => {
    queueRef.current = queueRef.current.filter(i => i.key !== key)
    bump()
  }

  // ── render ──
  if (stage === 'loading') {
    return <Centered icon="⏳" title="Loading…"><p className="text-sm text-gray-500">One moment.</p></Centered>
  }
  if (stage === 'missing_token') {
    return (
      <Centered icon="🔗" title="Please use the link from your email">
        <p className="text-sm text-gray-600">
          This page needs the full address from the email {firmName || 'the firm'} sent you, including everything after the <code>#</code>.
          Copying only part of it will not work. If you cannot find the email, please call the office.
        </p>
      </Centered>
    )
  }
  if (stage === 'unavailable') {
    return (
      <Centered icon="🔒" title="This link is not available">
        <p className="text-sm text-gray-600">
          It may have expired, or {firmName || 'the firm'} may have replaced it with a newer one.
          Please call the office and ask for a fresh link — nothing you already sent has been lost.
        </p>
      </Centered>
    )
  }
  if (stage === 'error') {
    return (
      <Centered icon="⚠️" title="Something went wrong">
        <p className="text-sm text-gray-600">{error}</p>
      </Centered>
    )
  }

  if (stage === 'email_code') {
    return (
      <Shell>
        <Header firmName={firmName} title="Confirm it’s you" subtitle="We’ll email you a 6-digit code" />
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            For your protection, {firmName || 'the firm'} asks for a code before you send records.
            We’ll send it to the same address that received this link.
          </p>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>}
          {!codeSent ? (
            <button type="button" onClick={requestCode} disabled={busy}
              className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold disabled:opacity-50">
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          ) : (
            <form onSubmit={submitCode} className="space-y-3">
              <label htmlFor="ru-code" className="block text-sm font-medium text-gray-700">6-digit code</label>
              <input id="ru-code" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" autoFocus
                className="w-full text-center text-2xl tracking-[0.4em] font-mono py-3 border rounded-xl"
                placeholder="000000" />
              <button type="submit" disabled={busy || code.trim().length !== 6}
                className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold disabled:opacity-50">
                {busy ? 'Checking…' : 'Continue'}
              </button>
              <button type="button" onClick={requestCode} disabled={busy || cooldownLeft > 0}
                className="w-full py-2 text-sm text-primary-700 disabled:opacity-50">
                {cooldownLeft > 0 ? `Send another code in ${cooldownLeft}s` : 'Send another code'}
              </button>
            </form>
          )}
        </div>
      </Shell>
    )
  }

  if (stage === 'consent') {
    const lines = Array.isArray(info?.consent_copy) ? info.consent_copy : []
    return (
      <Shell>
        <Header firmName={firmName} title="Before you send records" subtitle="Please read this — it is short" />
        <div className="p-5 space-y-4">
          <ul className="space-y-3">
            {lines.map((line, i) => (
              <li key={i} className="text-sm text-gray-800 leading-relaxed flex gap-2">
                <span className="text-gray-400 flex-shrink-0" aria-hidden="true">•</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          {info?.retention_date_display && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
              <strong>If we do not take your case, these records are destroyed on or before {info.retention_date_display}.</strong>{' '}
              Please keep your own originals.
            </div>
          )}
          {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>}
          <form onSubmit={acceptConsent} className="space-y-3 pt-1">
            <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)}
                className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <span>I have read the above and I understand that sending records does not make me a client.</span>
            </label>
            <div>
              <label htmlFor="ru-initials" className="block text-sm font-medium text-gray-700 mb-1">Type your initials</label>
              <input id="ru-initials" value={initials} onChange={e => setInitials(e.target.value.slice(0, 8))}
                autoComplete="off" placeholder="e.g. JM"
                className="w-32 py-3 px-3 border rounded-xl text-lg uppercase tracking-widest" />
            </div>
            <button type="submit" disabled={busy || !consentChecked || initials.trim().length < 2}
              className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold disabled:opacity-50">
              {busy ? 'Saving…' : 'I agree — continue'}
            </button>
          </form>
        </div>
      </Shell>
    )
  }

  // ── upload ──
  const q = queueRef.current
  const active = q.filter(i => i.status !== 'done')
  const inFlight = q.some(i => i.status === 'uploading')
  // A 503 (scanner down) or a 429 (too fast) stops the pump and leaves the
  // rest of the batch queued.  Without this the files just sit there looking
  // like they are about to send and never do — so offer the restart
  // explicitly rather than hoping someone picks the files again.
  const waiting = q.filter(i => i.status === 'queued').length
  const stalled = waiting > 0 && !inFlight

  return (
    <Shell>
      <Header firmName={firmName} title="Send us your records"
        subtitle={info?.retention_date_display ? `Destroyed on or before ${info.retention_date_display} if we do not take your case` : undefined} />

      <div className="p-5 space-y-4">
        {locked && (
          <div className="rounded-lg bg-red-50 border border-red-300 text-red-800 text-sm px-3 py-3" role="alert">
            {locked}
          </div>
        )}
        {!locked && !uploadsOpen && (
          <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm px-3 py-3" role="alert">
            {info?.uploads_closed_message
              || 'We cannot accept files at this moment. Please try again shortly, or call the office — your records have NOT been received.'}
          </div>
        )}
        {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" role="alert">{error}</div>}

        {!locked && (
          <>
            <div className="rounded-xl border-2 border-dashed border-gray-300 p-5 text-center bg-gray-50">
              <div className="text-3xl mb-2" aria-hidden="true">📄</div>
              <p className="text-sm text-gray-600 mb-3">
                Photos of your documents are fine. You can pick several at a time.
              </p>
              <input ref={fileInputRef} id="ru-files" type="file" multiple accept={ACCEPT_ATTR}
                className="sr-only"
                disabled={!uploadsOpen}
                onChange={e => addFiles(e.target.files)} />
              <label htmlFor="ru-files"
                className={`inline-block w-full py-3 rounded-xl font-semibold cursor-pointer ${uploadsOpen ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}>
                Choose files
              </label>
              <p className="text-[11px] text-gray-500 mt-3">
                PDF or photos (JPEG, PNG, GIF, TIFF, HEIC) · up to {fmtBytes(maxBytes)} each
                {info?.max_files ? ` · up to ${info.max_files} files` : ''}
              </p>
            </div>

            {active.length > 0 && (
              <div className="space-y-2" aria-live="polite">
                {active.map(item => (
                  <div key={item.key} className="border rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{item.name}</div>
                        <div className="text-[11px] text-gray-500">{fmtBytes(item.size)}</div>
                      </div>
                      <StatusPill status={item.status} />
                    </div>
                    {item.status === 'uploading' && (
                      <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-sky-500 transition-all" style={{ width: `${item.pct}%` }} />
                      </div>
                    )}
                    {item.error && <div className="mt-1.5 text-[12px] text-red-700">{item.error}</div>}
                    {item.status === 'failed' && (
                      <div className="mt-2 flex gap-3">
                        {item.file && (
                          <button type="button" onClick={() => retry(item.key)}
                            className="text-sm font-medium text-primary-700">Retry</button>
                        )}
                        <button type="button" onClick={() => removeFromQueue(item.key)}
                          className="text-sm text-gray-500">Remove</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Client-held receipts (D5).  We never ask the server what it holds. */}
        {sent.length > 0 && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <div className="text-sm font-semibold text-emerald-900 mb-2">
              {sent.length} {sent.length === 1 ? 'file' : 'files'} sent from this device
            </div>
            <ul className="space-y-1.5">
              {sent.map((s, i) => (
                <li key={`${s.sha256 || s.name}-${i}`} className="text-[12px] text-emerald-900">
                  <div className="font-medium truncate">✓ {s.name}</div>
                  <div className="text-emerald-800/70">
                    {fmtBytes(s.size)}{s.at ? ` · ${fmtTime(s.at)}` : ''}
                    {s.sha256 ? <> · <span className="font-mono">{String(s.sha256).slice(0, 12)}…</span></> : null}
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-emerald-800/80 mt-2">
              This receipt is stored on this device only, so it disappears if you clear your browser.
              {firmName ? ` ${firmName} keeps its own record of what arrived.` : ''}
            </p>
          </div>
        )}

        {!locked && stalled && (
          <button type="button" onClick={() => { setError(''); pump() }}
            className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold disabled:opacity-50">
            Send {waiting} waiting {waiting === 1 ? 'file' : 'files'}
          </button>
        )}
        {inFlight && <p className="text-xs text-gray-500 text-center">Sending — please keep this page open.</p>}

        <p className="text-[11px] text-gray-500 leading-relaxed border-t pt-3">
          This link is not a login. Please do not forward it — anyone who has it can send us files.
          Sending records does not make you a client.
        </p>
      </div>
    </Shell>
  )
}
